import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useGroupFilter } from 'pared/Routes/renderer/groupFilter'

import { useDateFilter } from '../../../dateFilter'
import calc from '../../../utils/calc'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

type IMetricDataType = {
  businessQuarter: number
  businessMonth: number
  businessWeek: number
  businessWeekOfMonth: number
  metricData: Record<
    string,
    {
      name: string
      unit: string
      value: number
    }
  >
  endDate: string
}

type IDataType =
  | {
      trendLocationGroupMetricValues: {
        nodes: IMetricDataType[]
      }
    }
  | {
      trendLocationMetricValues: {
        nodes: IMetricDataType[]
      }
    }

interface IReturnType {
  period: string
  value: number | null
}

const currentYearQuery = gql`
  query trendLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }

    trendLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const priorYearQuery = gql`
  query trendPriorYearLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }

    trendLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

export const winSssConfigs = {
  period: 'string',
  value: 'percent',
  valueLastYear: 'percent',
  valueTwoYears: 'percent',
  valueTwoYearsStacked: 'percent',
} as const

const useWinSss = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const yesterday = moment().subtract(1, 'day')
  const adjustedEndDate = moment(endDate, 'YYYY-MM-DD').isAfter(yesterday)
    ? yesterday.format('YYYY-MM-DD')
    : endDate

  const groupBy = variables.farwestTimeinterval?.value[0][0] || 'business_month'

  const dateList = ((groupBy: string) => {
    if (groupBy === 'business_week')
      return variables.date?.getCalendar('week', 'year')?.ids || [[]]
    else if (groupBy === 'business_quarter') return ['Q1', 'Q2', 'Q3', 'Q4']
    else return variables.date?.getCalendar('period', 'year')?.ids || [[]]
  })(groupBy)

  const metrics = variables.selectOptions?.orderMode?.value[0][0]

  const iFilter = hasGroupBy
    ? {
        location_group_ids: groupFilter?.ids,
        intersected_location_group_ids: groupFilter?.intersectedIds,
        metrics: [`${metrics}_percentage_diff_to_last_year`],
      }
    : {
        location_ids: groupFilter?.ids,
        metrics: [`${metrics}_percentage_diff_to_last_year`],
      }

  const { data: currentData, loading: currentLoading } = useQuery<IDataType>(
    currentYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: adjustedEndDate,
        iGroupBy: groupBy,
        iFilter,
        hasGroupBy,
      },
      skip: !startDate || !adjustedEndDate || !variables.date,
    },
  )

  const { data: priorData, loading: priorLoading } = useQuery<IDataType>(
    priorYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iGroupBy: groupBy,
        iFilter: {
          ...iFilter,
          use_yoy: true,
        },
        intersected_location_group_ids: groupFilter?.intersectedIds,
        hasGroupBy,
      },
      skip: !startDate || !endDate || !variables.date,
    },
  )

  const loading = currentLoading || priorLoading

  return {
    data: useMemo((): IApiDataType => {
      const currentDataGroupBy = hasGroupBy
        ? currentData &&
          'trendLocationGroupMetricValues' in currentData &&
          currentData.trendLocationGroupMetricValues?.nodes
        : currentData &&
          'trendLocationMetricValues' in currentData &&
          currentData.trendLocationMetricValues?.nodes
      const priorDataGroupBy = hasGroupBy
        ? priorData &&
          'trendLocationGroupMetricValues' in priorData &&
          priorData.trendLocationGroupMetricValues?.nodes
        : priorData &&
          'trendLocationMetricValues' in priorData &&
          priorData.trendLocationMetricValues?.nodes

      if (!currentDataGroupBy || !priorDataGroupBy || !metrics) return null

      const metricData = currentDataGroupBy.map((item): IReturnType => {
        const periodEndDate = moment(item.endDate)
        const yesterday = moment().subtract(1, 'days')
        const period = ((groupBy: string) => {
          if (groupBy === 'business_week')
            return `P${item.businessMonth}W${item.businessWeekOfMonth}`
          else if (groupBy === 'business_quarter')
            return `Q${item.businessQuarter}`
          else return `P${item.businessMonth}`
        })(groupBy)

        return {
          period,
          value: yesterday.isAfter(periodEndDate)
            ? item.metricData?.[`${metrics}_percentage_diff_to_last_year`]
                .value * 100
            : null,
        }
      })

      const priorMetricData = priorDataGroupBy.map((item): IReturnType => {
        const period = ((groupBy: string) => {
          if (groupBy === 'business_week')
            return `P${item.businessMonth}W${item.businessWeekOfMonth}`
          else if (groupBy === 'business_quarter')
            return `Q${item.businessQuarter}`
          else return `P${item.businessMonth}`
        })(groupBy)

        return {
          period,
          value:
            item.metricData?.[`${metrics}_percentage_diff_to_last_year`].value *
            100,
        }
      })

      return dateList
        .map((date) => {
          const currentData = metricData.find((d) => d.period === date)
          const sameDaylyData = priorMetricData.find((d) => d.period === date)

          return {
            period: date,
            value: currentData?.value,
            valueLastYear: sameDaylyData?.value,
            valueTwoYearsStacked: calc(
              currentData?.value,
              '+',
              sameDaylyData?.value,
            ),
          }
        })
        .filter((d) => d.period)
    }, [currentData, priorData]),
    loading,
  }
}

export default useWinSss
